/**
 * MCP proxy helpers — creates synthetic HTTP request/response objects
 * to forward JSON-RPC messages to the local AgentVaultMcpServer
 * when they arrive via WebSocket relay (from the backend MCP proxy).
 */
import { Readable } from "node:stream";
import type { IncomingMessage } from "node:http";
import type { AgentVaultMcpServer } from "./mcp-server.js";

/**
 * Forward a JSON-RPC payload to the MCP server by creating synthetic
 * HTTP request/response objects that the Streamable HTTP transport expects.
 *
 * Returns the MCP server's JSON-RPC response.
 */
export async function createMcpProxyRequest(
  mcpServer: AgentVaultMcpServer,
  payload: unknown,
): Promise<unknown> {
  const bodyStr = JSON.stringify(payload);

  // Create a synthetic IncomingMessage (readable stream with headers)
  const req = Object.assign(new Readable({ read() {} }), {
    method: "POST",
    url: "/mcp",
    headers: {
      "content-type": "application/json",
      "content-length": String(Buffer.byteLength(bodyStr)),
      // Use localhost to bypass SPT validation (WS already authenticated)
      host: "localhost",
    },
    socket: { remoteAddress: "127.0.0.1" },
  }) as unknown as IncomingMessage;

  // Push the body data
  (req as any).push(bodyStr);
  (req as any).push(null);

  // Create a synthetic ServerResponse that captures the output
  let responseStatus = 200;
  let responseBody = "";
  const chunks: string[] = [];

  const res = {
    writeHead: (status: number) => { responseStatus = status; },
    write: (chunk: string | Buffer) => {
      chunks.push(typeof chunk === "string" ? chunk : chunk.toString());
      return true;
    },
    end: (data?: string | Buffer) => {
      if (data) chunks.push(typeof data === "string" ? data : data.toString());
      responseBody = chunks.join("");
    },
    setHeader: () => {},
    getHeader: () => undefined,
    statusCode: 200,
    headersSent: false,
  } as any;

  await mcpServer.handleRequest(req, res);

  // Parse the response
  if (responseBody) {
    try {
      return JSON.parse(responseBody);
    } catch {
      return { jsonrpc: "2.0", error: { code: -32603, message: responseBody } };
    }
  }

  return null;
}
